<template>
  <div>
    <div class="text-subtitle-2" v-if="showTitle">{{$t('report_date_shortcuts')}}</div>
    <div class="my-2">
      <v-btn
        small
        v-for="shortcut in shortcuts"
        :key="shortcut.name"
        @click="setRange(shortcut.lowerBound, shortcut.upperBound)"
        class="mr-2 mb-2">
        {{ shortcut.name }}
      </v-btn>
    </div>
  </div>
</template>

<script>

import Moment from 'moment-timezone'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'

/**
 * Provides shortcut links to set pre-defined date ranges
 */
export default {
  name: 'DateSelectorShortcuts',
  mixins: [CurrentUserMixin],
  props: {
    showTitle: {
      type: Boolean,
      default: true
    },
    lowerBound: {
      validator: (value) => {
        return value === null || value instanceof Date || typeof value === 'string' || value instanceof String || Moment.isMoment(value)
      }
    },
    upperBound: {
      validator: (value) => {
        return value === null || value instanceof Date || typeof value === 'string' || value instanceof String || Moment.isMoment(value)
      }
    },
    /**
     * An optional timezone parameter. If selected, the selected dates will be in the provided timezone, otherwise the
     * current customer context will be used.
     */
    timezone: {
      type: String
    }
  },
  computed: {
    /**
     * Internally computed timezone field to use the timezone prop if provided, or otherwise default to the current
     * customer context.
     * @returns {String|*}
     */
    computedTimezone () {
      if (this.timezone) {
        return this.timezone
      } else {
        return this.customerContext.timezone()
      }
    }
  },
  data () {
    return {
      shortcuts: [
        {
          name: this.$t('today'),
          lowerBound: () => Moment().tz(this.computedTimezone).startOf('day'),
          upperBound: () => Moment().tz(this.computedTimezone).endOf('day')
        },
        {
          name: this.$t('yesterday'),
          lowerBound: () => Moment().tz(this.computedTimezone).subtract(1, 'days').startOf('day'),
          upperBound: () => Moment().tz(this.computedTimezone).subtract(1, 'days').endOf('day')
        },
        {
          name: this.$t('last_7_days'),
          lowerBound: () => Moment().tz(this.computedTimezone).subtract(6, 'days').startOf('day'),
          upperBound: () => Moment().tz(this.computedTimezone).endOf('day')
        },
        {
          name: this.$t('last_30_days'),
          // Subtract 29, not 30: the range is inclusive of both bounds, so today plus the previous 29 days is 30 days.
          lowerBound: () => Moment().tz(this.computedTimezone).subtract(29, 'days').startOf('day'),
          upperBound: () => Moment().tz(this.computedTimezone).endOf('day')
        },
        {
          name: this.$t('month_to_date'),
          lowerBound: () => Moment().tz(this.computedTimezone).startOf('month'),
          upperBound: () => Moment().tz(this.computedTimezone).endOf('day')
        },
        {
          name: this.$t('previous_month'),
          lowerBound: () => Moment().tz(this.computedTimezone).startOf('month').subtract(1, 'minutes').startOf('month'),
          upperBound: () => Moment().tz(this.computedTimezone).startOf('month').subtract(1, 'minutes')
        },
        {
          name: this.$t('year_to_date'),
          lowerBound: () => Moment().tz(this.computedTimezone).startOf('year'),
          upperBound: () => Moment().tz(this.computedTimezone).endOf('day')
        },
        {
          name: this.$t('previous_year'),
          lowerBound: () => Moment().tz(this.computedTimezone).startOf('year').subtract(1, 'minutes').startOf('year'),
          upperBound: () => Moment().tz(this.computedTimezone).startOf('year').subtract(1, 'minutes')
        }
      ]
    }
  },
  methods: {
    setRange (lowerBound, upperBound) {
      this.$emit('update:lowerBound', lowerBound())
      this.$emit('update:upperBound', upperBound())
    }
  }
}
</script>

<style>
</style>
